// src/components/Informascota.jsx
import petfamLogo from "../assets/PetfamLogo.png";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  X,
  Pencil,
  ArrowRight,
  Check,
  Plus,
  Trash2,
  ShieldCheck,
  Syringe,
  Scale,
  Cake,
} from "lucide-react";

import Cropper from "react-easy-crop";
import { authFetch } from "../utils/authFetch";
import petfamBrand from "../assets/Petfamp.png";

import "../styles/informascota.css";

import golden from "../assets/golden.webp";
import { getBreedImage } from "../config/petBreedImages";

import { QRCodeCanvas } from "qrcode.react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import config from "../config";
import { createPortal } from "react-dom";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

// =======================================================
// ✅ Rango de peso estándar por raza (kg) — estándares de raza públicos,
// no un dato inventado por mascota. Se usa solo para el análisis de peso
// de la pestaña Salud. Claves normalizadas (minúsculas, sin tildes).
// =======================================================
const BREED_WEIGHT_RANGES_KG = {
  "golden retriever": [25, 34],
  labrador: [25, 36],
  "labrador retriever": [25, 36],
  pomerania: [1.9, 3.5],
  pomeranian: [1.9, 3.5],
  "bulldog frances": [8, 14],
  "french bulldog": [8, 14],
  chihuahua: [1.5, 3],
  beagle: [9, 11],
  "pastor aleman": [22, 40],
  "german shepherd": [22, 40],
  husky: [16, 27],
  "siberian husky": [16, 27],
  yorkshire: [2, 3.5],
  schnauzer: [5, 9],
  boxer: [25, 32],
  rottweiler: [35, 60],
  dachshund: [3, 5],
  salchicha: [3, 5],
  "shih tzu": [4, 8],
  "cocker spaniel": [12, 16],
  gato: [3, 5],
  "gato comun": [3, 5],
  "gato domestico": [3, 5],
};

function normalizeBreedKey(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

// ✅ Compara el peso registrado contra el rango estándar de la raza.
// Devuelve null si no hay datos suficientes (raza o peso ausentes).
function getWeightAnalysis(breedRaw, weightKgRaw) {
  const weight = parseFloat(String(weightKgRaw || "").replace(/kg/gi, "").replace(",", "."));
  if (!breedRaw || !Number.isFinite(weight) || weight <= 0) return null;

  const key = normalizeBreedKey(breedRaw);
  const matchKey = Object.keys(BREED_WEIGHT_RANGES_KG).find(
    (k) => key.includes(k) || k.includes(key)
  );

  if (!matchKey) return { known: false };

  const [min, max] = BREED_WEIGHT_RANGES_KG[matchKey];
  let status;
  let percent;

  if (weight < min) {
    status = "Bajo peso";
    percent = Math.round((weight / min) * 100);
  } else if (weight > max) {
    status = "Sobrepeso";
    percent = Math.round((max / weight) * 100);
  } else {
    status = "Peso ideal";
    percent = 100;
  }

  percent = Math.max(0, Math.min(100, percent));

  return { known: true, status, percent, min, max, weight };
}

const carnetInputStyle = {
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  minWidth: 0,
  height: 58,
  borderRadius: 18,
  border: "1px solid #d7dee2",
  background: "#fff",
  padding: "0 24px",
  fontFamily: "Poppins",
  fontSize: 16,
  fontWeight: 500,
  color: "#0B2F3E",
  outline: "none",
  WebkitAppearance: "auto",
  appearance: "auto",
};

const MONTH_ABBR_ES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

// =======================================================
// CALCULAR EDAD
// =======================================================
function getAgeLabel(birthDateStr) {
  if (!birthDateStr) return "Edad no registrada";

  const s = String(birthDateStr).trim().slice(0, 10);
  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return "Edad no registrada";

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  // ✅ crear fecha LOCAL, no UTC
  const birthDate = new Date(year, month - 1, day);

  if (
    isNaN(birthDate.getTime()) ||
    birthDate.getFullYear() !== year ||
    birthDate.getMonth() !== month - 1 ||
    birthDate.getDate() !== day
  ) {
    return "Edad no registrada";
  }

  const today = new Date();
  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();

  if (days < 0) {
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += prevMonth.getDate();
    months -= 1;
  }

  if (months < 0) {
    months += 12;
    years -= 1;
  }

  if (years < 0) return "Fecha inválida";

  if (years === 0 && months === 0) {
    if (days <= 0) return "Menos de 1 día";
    return days === 1 ? "1 día" : `${days} días`;
  }

  if (years === 0) return `${months} ${months === 1 ? "mes" : "meses"}`;
  if (months === 0) return `${years} ${years === 1 ? "año" : "años"}`;

  return `${years} ${years === 1 ? "año" : "años"} y ${months} ${
    months === 1 ? "mes" : "meses"
  }`;
}

// ✅ Fecha corta tipo "14 jul" para la agenda
function formatDateShort(iso) {
  if (!iso) return "";
  const base = String(iso).trim().slice(0, 10);
  const [y, m, d] = base.split("-").map(Number);
  if (!y || !m || !d) return "";
  return `${d} ${MONTH_ABBR_ES[m - 1]}`;
}

// ✅ Frase del subtítulo según el % real de salud/cuidados de la mascota
// (mismo dato que alimenta la barra "CUIDADOS" de la tarjeta en Interfaz).
// Ya NO se usa en el render (se quitó la frase de ánimo bajo el nombre),
// se deja aquí por si se vuelve a necesitar.
function getMoodFromSaludPercent(percent) {
  if (percent == null) return "Cargando información...";
  if (percent >= 90) return "Con energía de sobra";
  if (percent >= 70) return "Feliz y saludable";
  if (percent >= 50) return "Bien, aunque puede mejorar";
  if (percent >= 30) return "Necesita más cuidados";
  return "Requiere atención pronto";
}

// =======================================================
// OBTENER ID DEL USUARIO
// =======================================================
function getCurrentUserId() {
  try {
    const keys = ["petfamUser", "user", "userData", "loginUser", "idUser"];
    for (const k of keys) {
      const raw = localStorage.getItem(k);
      if (!raw) continue;

      if (k === "idUser") {
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
      }

      const parsed = JSON.parse(raw);
      if (parsed.idUser) return parsed.idUser;
      if (parsed.id) return parsed.id;
      if (parsed.userId) return parsed.userId;
    }
  } catch {}
  return null;
}

// =======================================================
// ✅ PERSISTIR MASCOTA ACTIVA (para volver desde Medismart)
// =======================================================
function getLastPetIdKey(idUser) {
  return idUser ? `petfam:lastPetId:${idUser}` : "petfam:lastPetId";
}

function readLastPetId(idUser) {
  try {
    const raw = localStorage.getItem(getLastPetIdKey(idUser));
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null; // ✅ 0 se vuelve null
  } catch {
    return null;
  }
}

function toISODateOnly(v) {
  if (!v) return "";
  const s = String(v);
  if (s.includes("T")) return s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  try {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  } catch {}
  return "";
}

const normStatus = (s) => String(s || "").trim().toUpperCase();
const isDoneStatus = (s) => normStatus(s) === "HECHO" || normStatus(s) === "DONE";

const makeRowId = () =>
  `ROW-${Date.now()}-${Math.random().toString(16).slice(2)}`;

function upper(v) {
  return String(v || "").toUpperCase();
}

function getPetSpeciesForVaccines(pet) {
  const raw = String(
    pet?.species ||
      pet?.tipo ||
      pet?.type ||
      pet?.animalType ||
      ""
  ).toLowerCase();

  return raw.includes("gat") ? "GATO" : "PERRO";
}

// =======================================================
// URL -> BASE64 (para poder meter imágenes en jsPDF)
// =======================================================
async function imageUrlToBase64(url) {
  if (!url) throw new Error("URL vacía");

  if (String(url).startsWith("data:image/")) return url;

  if (String(url).startsWith("blob:")) {
    const res = await fetch(url);
    if (!res.ok) throw new Error("No se pudo cargar la imagen (blob)");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  let finalUrl = String(url);
  if (finalUrl.startsWith("/uploads/")) {
    finalUrl = `${API_BASE_URL}${finalUrl}`;
  } else if (finalUrl.startsWith("uploads/")) {
    finalUrl = `${API_BASE_URL}/${finalUrl}`;
  }

  const res = await fetch(finalUrl, { mode: "cors" });
  if (!res.ok) throw new Error(`No se pudo cargar la imagen (${res.status})`);

  const blob = await res.blob();

  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// ===============================
// ✅ CELDA EDITABLE (BD) - Veterinario
// ===============================
function VeterinarianCell({
  API_BASE_URL,
  rowId,
  bdEventId,
  initialValue,
  disabled,
  onSaved,
}) {
  const [val, setVal] = React.useState(initialValue || "");
  const tRef = React.useRef(null);

  React.useEffect(() => {
    setVal(initialValue || "");
  }, [initialValue, bdEventId]);

  const save = async (nextVal) => {
    if (!bdEventId) return;

    try {
      const res = await authFetch(`/calendar/events/${bdEventId}/veterinarian`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ veterinarian: nextVal || "" }),
      });

      if (!res.ok) throw new Error(`Backend respondió ${res.status}`);
      onSaved?.(rowId, nextVal || "");
    } catch (e) {
      console.error(e);
      // ✅ No quitamos foco ni borramos texto
    }
  };

  const handleChange = (e) => {
    const nextVal = e.target.value;
    setVal(nextVal);

    if (tRef.current) window.clearTimeout(tRef.current);
    tRef.current = window.setTimeout(() => save(nextVal), 600);
  };

  return (
    <input
      className="nav-input"
      style={{ height: 42, flex: 1 }}
      value={val}
      onChange={handleChange}
      disabled={disabled}
      placeholder="Nombre del veterinario"
      title="Se guarda automáticamente en la BD"
    />
  );
}

// ===============================
// ✅ CARNET - COMPONENTE FUERA (NO SE DESMONTA)
// ===============================
const CarnetContent = React.memo(function CarnetContent({
  API_BASE_URL,

  // estado
  carnetRows,
  loadingCarnet,
  carnetError,
  savingCarnet,

  // acciones carnet
  addCarnetRow,
  updateCarnetField,
  saveNewCarnetRow,
  removeCarnetRow,
deleteCarnetRowFromBackend,
deleteMode,
toggleDeleteMode,
setCarnetRows,

  // QR / PDF
  loadingPublicId,
  carnetPublicUrl,
  downloadCarnetPdf,
}) {
  return (
    <>
      {/* ✅ Header con + Añadir */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <h3 className="vacc-title" style={{ margin: 15 }}>
          Carnet de vacunación
        </h3>

        <div style={{ display: "flex", gap: 8, marginRight: 28 }}>
  <button
    type="button"
    onClick={toggleDeleteMode}
    disabled={savingCarnet || carnetRows.length === 0}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      border: "none",
      cursor: "pointer",
      padding: "5px 14px",
      borderRadius: 999,
      background: deleteMode ? "#C0392B" : "#fff",
      color: deleteMode ? "#fff" : "#C0392B",
      fontFamily: "Poppins",
      fontWeight: 600,
      boxShadow: "0 10px 24px rgba(0,0,0,.12)",
      whiteSpace: "nowrap",
      opacity: savingCarnet || carnetRows.length === 0 ? 0.6 : 1,
    }}
  >
    <Trash2 size={16} />
    Eliminar
  </button>

  <button
    type="button"
    onClick={addCarnetRow}
    disabled={savingCarnet}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      border: "none",
      cursor: "pointer",
      padding: "5px 14px",
      borderRadius: 999,
      background: "#0F6B7A",
      color: "#fff",
      fontFamily: "Poppins",
      fontWeight: 600,
      boxShadow: "0 10px 24px rgba(0,0,0,.12)",
      whiteSpace: "nowrap",
      opacity: savingCarnet ? 0.6 : 1,
    }}
    title="Añadir vacuna en este carnet"
  >
    <Plus size={16} />
    Añadir
  </button>
</div>
      </div>

      {loadingCarnet && (
        <p className="pf-cal-event-desc" style={{ marginTop: 10 }}>
          Cargando carnet…
        </p>
      )}

      {carnetError && (
        <p className="pf-cal-event-desc" style={{ marginTop: 10, color: "#c0392b" }}>
          {carnetError}
        </p>
      )}

      <div className="vacc-table-wrap">
        <div className="vacc-table" style={{ marginTop: 12 }}>
          <div
  className="vacc-header"
  style={{
    gridTemplateColumns: deleteMode
      ? "44px 160px 1.6fr 1.2fr"
      : "160px 1.6fr 1.2fr",
  }}
>
  {deleteMode && <span></span>}
            <span>Fecha</span>
            <span>Vacuna</span>
            <span>Veterinario</span>
          </div>

          {carnetRows.length === 0 ? (
            <div className="vacc-row" style={{ gridTemplateColumns: "1fr", opacity: 0.85 }}>
              <span>
                Aún no tienes vacunas en <strong>HECHO</strong> para esta mascota. Márcalas como
                aplicada desde el calendario.
              </span>
            </div>
          ) : (
            carnetRows.map((row) => {
              const isNew = !!row._isNew;

              return (
                <div
                  className="vacc-row"
                  key={row.id}
                  style={{
  gridTemplateColumns: deleteMode ? "44px 160px 1.6fr 1.2fr" : "160px 1.6fr 1.2fr",
  gap: 12,
  alignItems: "center",
}}
                >
                  {deleteMode && (
  <button
    type="button"
    onClick={() => deleteCarnetRowFromBackend(row)}
    disabled={savingCarnet}
    title="Eliminar vacuna"
    style={{
      width: 38,
      height: 38,
      borderRadius: 999,
      border: "none",
      background: "#C0392B",
      color: "#fff",
      display: "grid",
      placeItems: "center",
      cursor: "pointer",
      opacity: savingCarnet ? 0.6 : 1,
    }}
  >
    <Trash2 size={17} />
  </button>
)}
                  
                  {/* FECHA */}
                  <input
                    className="nav-input"
                    style={{ height: 42 }}
                    type="date"
                    value={row.date || ""}
                    disabled={!isNew}
                    onChange={(e) => updateCarnetField(row.id, "date", e.target.value)}
                    title={isNew ? "Fecha" : "Fecha (desde BD)"}
                  />

                  {/* VACUNA */}
                  <input
                    className="nav-input"
                    style={{ height: 42 }}
                    value={row.vaccine || ""}
                    disabled={!isNew}
                    onChange={(e) => updateCarnetField(row.id, "vaccine", e.target.value)}
                    placeholder={isNew ? "Nombre de la vacuna" : ""}
                    title={isNew ? "Vacuna" : "Vacuna (desde BD)"}
                  />

                  {/* VETERINARIO + acciones */}
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    {isNew ? (
                      <input
                        className="nav-input"
                        style={{ height: 42, flex: 1 }}
                        value={row.veterinarian || ""}
                        onChange={(e) => updateCarnetField(row.id, "veterinarian", e.target.value)}
                        placeholder="Nombre del veterinario"
                        title="Veterinario"
                      />
                    ) : (
                      <VeterinarianCell
                        API_BASE_URL={API_BASE_URL}
                        rowId={row.id}
                        bdEventId={row._bdEventId}
                        initialValue={row.veterinarian || ""}
                        disabled={false}
                        onSaved={(rid, v) => {
                          // ✅ sincroniza carnetRows UNA SOLA VEZ cuando el backend guarda
                          setCarnetRows((prev) =>
                            (Array.isArray(prev) ? prev : []).map((r) =>
                              r.id === rid ? { ...r, veterinarian: v } : r
                            )
                          );
                        }}
                      />
                    )}

                    {isNew && (
                      <>
                        <button
                          type="button"
                          onClick={() => saveNewCarnetRow(row)}
                          disabled={savingCarnet}
                          style={{
                            height: 42,
                            padding: "0 14px",
                            borderRadius: 999,
                            border: "none",
                            cursor: "pointer",
                            background: "#0F6B7A",
                            color: "#fff",
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            opacity: savingCarnet ? 0.6 : 1,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Guardar
                        </button>

                        <button
                          type="button"
                          onClick={() => removeCarnetRow(row.id)}
                          disabled={savingCarnet}
                          style={{
                            height: 42,
                            padding: "0 12px",
                            borderRadius: 999,
                            border: "1px solid rgba(11,47,62,.2)",
                            cursor: "pointer",
                            background: "#fff",
                            color: "#0B2F3E",
                            fontFamily: "Poppins",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Quitar
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <p className="pf-cal-event-desc" style={{ marginTop: 12, opacity: 0.75 }}>
        * Recuerda que este carnet <strong>no</strong> reemplaza un carnet en físico pero te ayuda a
        llevar un control de tu mascota.
      </p>

      <div className="carnet-actions">
        <div className="carnet-qr">
          {loadingPublicId ? (
            <span style={{ fontSize: 12, opacity: 0.7 }}>Generando QR…</span>
          ) : carnetPublicUrl ? (
            <>
              <QRCodeCanvas value={carnetPublicUrl} size={88} includeMargin />
              <div className="carnet-qr-text">
                <div className="carnet-qr-title">QR único de esta mascota</div>
                <div className="carnet-qr-sub">Al escanearlo, se verá solo este carnet.</div>
              </div>
            </>
          ) : (
            <span style={{ fontSize: 12, opacity: 0.7 }}>No se pudo generar el QR.</span>
          )}
        </div>

        <button type="button" className="carnet-pdf-btn" onClick={downloadCarnetPdf}>
          Descargar PDF
        </button>
      </div>
    </>
  );
});

function CarnetDateField({ value, onChange, placeholder = "Fecha" }) {
  const inputRef = React.useRef(null);

  const openPicker = () => {
    const input = inputRef.current;
    if (!input) return;

    try {
      if (typeof input.showPicker === "function") {
        input.showPicker();
      } else {
        input.focus();
        input.click();
      }
    } catch {
      input.focus();
      input.click();
    }
  };

  return (
    <div
      className="carnet-date-field"
      onClick={openPicker}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") openPicker();
      }}
    >
      <span className={value ? "has-value" : ""}>
        {value || placeholder}
      </span>

      <input
        ref={inputRef}
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export default function Informascota() {
  const drawPetfamWatermark = async (doc) => {
    try {
      const logoBase64 = await imageUrlToBase64(petfamLogo);

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      const size = 120;
      const opacity = 0.08;

      const gState = doc.GState({ opacity });
      doc.setGState(gState);

      doc.addImage(
        logoBase64,
        "PNG",
        pageWidth / 2 - size / 2,
        pageHeight / 2 - size / 2,
        size,
        size
      );

      doc.setGState(new doc.GState({ opacity: 1 }));
    } catch (e) {
      console.warn("No se pudo renderizar el watermark", e);
    }
  };

  const navigate = useNavigate();
  const location = useLocation();

  const [animClass, setAnimClass] = useState("petframe-enter");
  const [isVaccOpen, setIsVaccOpen] = useState(false);

  // ✅ Pestañas del nuevo diseño desktop (Resumen / Salud / Carnet)
  const [activeTab, setActiveTab] = useState("resumen");

  // ✅ Portada del carnet: se abre con animación hacia la izquierda.
  // Es solo un estado visual — no toca nada de la lógica de CarnetContent.
  const [carnetOpen, setCarnetOpen] = useState(false);

  const petFromState = location.state?.pet || null;
  const idPetFromState = location.state?.idPet || location.state?.petId || null;

  const idPetFromQuery = useMemo(() => {
    try {
      const qs = new URLSearchParams(location.search);
      const v = qs.get("idPet") || qs.get("petId");
      return v ? Number(v) : null;
    } catch {
      return null;
    }
  }, [location.search]);

  // ✅ si vuelves desde Medismart sin state ni query, recupera la última mascota
  const idPetFromStorage = useMemo(() => {
    const idUser = getCurrentUserId();
    return readLastPetId(idUser);
  }, []);

  const resolvedPetId =
    petFromState?.idPet ||
    petFromState?.id ||
    idPetFromState ||
    idPetFromQuery ||
    idPetFromStorage ||
    null;

  const [pet, setPet] = useState(() => petFromState);
  const [loadingPet, setLoadingPet] = useState(false);
  const [petError, setPetError] = useState("");

  useEffect(() => {
    const fetchPetIfNeeded = async () => {
      if (petFromState) {
        setPet(petFromState);
        return;
      }
          if (resolvedPetId == null) return; // ✅ solo corta si es null/undefined

      const idUser = getCurrentUserId();
      if (!idUser) {
        setPetError("No encontramos tu sesión. Inicia sesión nuevamente.");
        return;
      }

      try {
        setLoadingPet(true);
        setPetError("");

        const res = await authFetch(`/pets/user/${idUser}`);
        const json = await res.json();

        const list = json?.data || [];
        const found = list.find((p) => Number(p.idPet || p.id) === Number(resolvedPetId));

        if (!found) {
          setPetError(
            `No encontramos la mascota (idPet=${resolvedPetId}). Puede que haya sido eliminada.`
          );
          return;
        }

        setPet(found);
      } catch (e) {
        console.error(e);
        setPetError("No se pudo cargar la mascota. Intenta nuevamente.");
      } finally {
        setLoadingPet(false);
      }
    };

    fetchPetIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petFromState, resolvedPetId]);

  const fallbackPet = useMemo(
    () => ({
      idPet: 0,
      nombre: "Mascota",
      tipo: "Perro",
      raza: "Golden Retriever",
      sexo: "Macho",
      peso: "10",
      nacimiento: "2020-01-01",
      foto: golden,
    }),
    []
  );

  const safePet = pet || fallbackPet;

  const safePetId =
    safePet?.idPet || safePet?.id || safePet?.id_pet || resolvedPetId || null;

  const profilePhoto =
  safePet?.profilePhotoUrl ||
  safePet?.profilePhoto ||
  safePet?.fotoUrl ||
  safePet?.foto ||
  (safePet?.profilePhotoBase64
    ? `data:image/png;base64,${String(safePet.profilePhotoBase64).trim()}`
    : null);

  const heroImg =
    profilePhoto ||
    getBreedImage(safePet?.tipo || safePet?.species, safePet?.raza || safePet?.breed) ||
    golden;

  const petBirth =
    safePet?.nacimiento || safePet?.fechaNacimiento || safePet?.birthDate || "";

  const ageLabel = useMemo(() => getAgeLabel(petBirth), [petBirth]);

  // =======================================================
  // ✅ % DE SALUD REAL (mismo dato/endpoint que la barra "CUIDADOS" de la
  // tarjeta en Interfaz: /pets/{id}/care-percent). Se anima al entrar.
  // =======================================================
  const [saludPercent, setSaludPercent] = useState(null);
  const [animatedSalud, setAnimatedSalud] = useState(0);

  // ✅ Tooltip del "?" junto al % de CUIDADOS de esta pantalla (una sola
  // mascota, así que basta con un booleano).
  const [healthInfoOpen, setHealthInfoOpen] = useState(false);

  useEffect(() => {
    const idUser = getCurrentUserId();
    if (!idUser || !safePetId) return;

    let cancelled = false;

    (async () => {
      try {
        const res = await authFetch(`/pets/${safePetId}/care-percent?idUser=${idUser}`);
        const json = await res.json().catch(() => null);
        if (!cancelled && res.ok && typeof json?.data === "number") {
          setSaludPercent(json.data);
        }
      } catch (e) {
        console.error("Error cargando % de salud", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [safePetId]);

  // ✅ Anima el anillo hacia el valor real cada vez que llega/cambia
  useEffect(() => {
    if (saludPercent == null) return;
    const frame = requestAnimationFrame(() => setAnimatedSalud(saludPercent));
    return () => cancelAnimationFrame(frame);
  }, [saludPercent]);

  // =======================================================
  // ✅ AGENDA REAL: vacunas/desparasitaciones programadas (de
  // /calendar/events, igual que las notificaciones de salud en
  // Interfaz.jsx) + tareas básicas de hoy de ESTA mascota (de
  // /daily-care/today, igual que "Cuidados de hoy" en Interfaz.jsx).
  // =======================================================
  const [petAllEvents, setPetAllEvents] = useState([]); // vacunas + desparasitaciones, SIN recortar (para % de indicadores)
  const [petDailyTasks, setPetDailyTasks] = useState([]);
  const [loadingAgenda, setLoadingAgenda] = useState(false);

  useEffect(() => {
    const idUser = getCurrentUserId();
    if (!idUser || !safePetId) return;

    let cancelled = false;

    (async () => {
      try {
        setLoadingAgenda(true);

        const [evRes, taskRes] = await Promise.all([
          authFetch(`/calendar/events?idUser=${idUser}`),
          authFetch(`/daily-care/today?idUser=${idUser}`),
        ]);

        const evJson = await evRes.json().catch(() => null);
        const taskJson = await taskRes.json().catch(() => null);

        if (cancelled) return;

        // Vacunas y desparasitaciones de ESTA mascota (todas, sin recortar)
        const events = Array.isArray(evJson?.data) ? evJson.data : [];
        const petEvents = events
          .filter((ev) => {
            const idPet = ev?.idPet ?? ev?.id_pet ?? null;
            const type = String(ev?.eventType ?? ev?.event_type ?? "").trim().toUpperCase();
            return (
              Number(idPet) === Number(safePetId) &&
              (type === "VACCINE" ||
                type === "VACUNA" ||
                type === "DEWORMING_INTERNAL" ||
                type === "DEWORMING_EXTERNAL")
            );
          })
          .map((ev) => {
            const type = String(ev?.eventType ?? ev?.event_type ?? "").trim().toUpperCase();
            const isVaccine = type === "VACCINE" || type === "VACUNA";
            const date = toISODateOnly(ev?.eventDate ?? ev?.event_date ?? "");

            return {
              id: `event-${ev.idEvent ?? ev.id_event ?? ev.id ?? Math.random()}`,
              title: isVaccine ? ev.title || ev.vaccineName || "Vacuna" : "Desparasitación",
              date,
              done: isDoneStatus(ev?.status),
              kind: isVaccine ? "vaccine" : "deworming",
            };
          })
          .filter((ev) => !!ev.date);

        setPetAllEvents(petEvents);

        // Tareas básicas de hoy que pertenecen a esta mascota
        const tasks = Array.isArray(taskJson?.data?.tasks) ? taskJson.data.tasks : [];
        const filteredTasks = tasks.filter((t) => Number(t.idPet) === Number(safePetId));
        setPetDailyTasks(filteredTasks);
      } catch (e) {
        console.error("Error cargando agenda de la mascota", e);
      } finally {
        if (!cancelled) setLoadingAgenda(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [safePetId]);

  // ✅ Lista corta (próximas 4) que se muestra en la Agenda del Resumen
  const agendaEvents = useMemo(
    () =>
      [...petAllEvents]
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
        .slice(0, 4),
    [petAllEvents]
  );

  // =======================================================
  // ✅ INDICADORES REALES para la pestaña Salud
  // =======================================================
  const todayKeyForStats = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const vaccineStats = useMemo(() => {
    const items = petAllEvents.filter((e) => e.kind === "vaccine");
    if (items.length === 0) return null;
    const overdue = items.filter((e) => !e.done && e.date < todayKeyForStats).length;
    return { total: items.length, percent: Math.round(((items.length - overdue) / items.length) * 100) };
  }, [petAllEvents, todayKeyForStats]);

  const dewormingStats = useMemo(() => {
    const items = petAllEvents.filter((e) => e.kind === "deworming");
    if (items.length === 0) return null;
    const overdue = items.filter((e) => !e.done && e.date < todayKeyForStats).length;
    return { total: items.length, percent: Math.round(((items.length - overdue) / items.length) * 100) };
  }, [petAllEvents, todayKeyForStats]);

  const activityStats = useMemo(() => {
    if (petDailyTasks.length === 0) return null;
    const done = petDailyTasks.filter((t) => t.done).length;
    return { total: petDailyTasks.length, done, percent: Math.round((done / petDailyTasks.length) * 100) };
  }, [petDailyTasks]);

  // =======================================================
  // HEADER EDITABLE (✅ SOLO PESO)
  // =======================================================
  const [headerEditing, setHeaderEditing] = useState(false);
  const [headerValues, setHeaderValues] = useState({
    sexo: safePet.sexo || safePet.sex || "",
    peso: safePet.peso || safePet.weightKg || "",
    nacimiento: petBirth || "",
  });

  useEffect(() => {
    if (!pet) return;

    const newBirth = pet?.nacimiento || pet?.fechaNacimiento || pet?.birthDate || "";

    setHeaderValues({
      sexo: pet.sexo || pet.sex || "",
      peso: pet.peso || pet.weightKg || "",
      nacimiento: newBirth || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pet]);

  // ✅ Análisis de peso vs. estándar de raza (pestaña Salud) — depende de
  // headerValues.peso, así que va después de declararlo.
  const weightAnalysis = useMemo(
    () => getWeightAnalysis(safePet.raza || safePet.breed, headerValues.peso),
    [safePet.raza, safePet.breed, headerValues.peso]
  );

  const handleHeaderChange = (field, value) => {
    if (field !== "peso") return;
    const cleaned = String(value).replace(/[^\d.,]/g, "");
    setHeaderValues((prev) => ({ ...prev, peso: cleaned }));
  };

  // ✅ solo guarda peso (PATCH recomendado)
  const savePetUpdatesToBackend = async () => {
    try {
      const cleanPeso = String(headerValues.peso || "")
        .replace(/kg/gi, "")
        .replace(",", ".")
        .trim();

      const n = Number(cleanPeso);
      if (!Number.isFinite(n) || n <= 0) return;

      const idToUpdate = safePet?.idPet || safePetId;
      if (!idToUpdate) return;

      const body = { weightKg: n };

      // ✅ PATCH /pets/{id}/weight
      const patchRes = await authFetch(`/pets/${idToUpdate}/weight`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      // fallback (compatibilidad) si algo intermedio bloquea PATCH
      if (!patchRes.ok) {
        await authFetch(`/pets/${idToUpdate}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      }
    } catch {}
  };

  const toggleHeaderEdit = async () => {
    if (headerEditing) await savePetUpdatesToBackend();
    setHeaderEditing((prev) => !prev);
  };

  const handleClose = () => setAnimClass("petframe-exit");

  const handleAnimationEnd = () => {
    if (animClass === "petframe-exit") {
      navigate("/interfaz", { replace: true });
    }
  };

  // =======================================================
  // CARNET: cargar HECHO/VACCINE de BD
  // =======================================================
  const [carnetRows, setCarnetRows] = useState([]);
  const [loadingCarnet, setLoadingCarnet] = useState(false);
  const [carnetError, setCarnetError] = useState("");

  // ✅ debounce limpio (arregla que “no deje escribir seguido”)
  const saveTimersRef = useRef(new Map());

  const saveVeterinarianToBackend = async (idEvent, veterinarian) => {
    if (!idEvent) return;

    try {
      const res = await authFetch(`/calendar/events/${idEvent}/veterinarian`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ veterinarian }),
      });

      if (!res.ok) throw new Error(`Backend respondió ${res.status}`);
    } catch (e) {
      console.error(e);
      setCarnetError("No se pudo guardar el veterinario. Revisa tu conexión e intenta de nuevo.");
      window.setTimeout(() => setCarnetError(""), 2600);
    }
  };

  const loadCarnet = async () => {
    const idUser = getCurrentUserId();
    if (!idUser || !safePetId) return;

    try {
      setLoadingCarnet(true);
      setCarnetError("");

      const res = await authFetch(`/calendar/events?idUser=${idUser}`);
      const json = await res.json();
      const data = Array.isArray(json?.data) ? json.data : [];

      const doneForPet = data
        .filter((ev) => {
          const idPet = ev?.idPet ?? ev?.id_pet ?? null;
          const type = String(ev?.eventType ?? ev?.event_type ?? "").toUpperCase();
          return Number(idPet) === Number(safePetId) && type === "VACCINE" && isDoneStatus(ev?.status);
        })
        .map((ev) => {
          const idEvent = ev?.idEvent ?? ev?.id_event ?? ev?.id ?? null;
          const date = toISODateOnly(ev?.eventDate ?? ev?.event_date ?? "");
          const vaccine = ev?.title || "Vacuna";
          const veterinarian = ev?.veterinarian ?? "";

          return {
            id: `BD-${idEvent ?? Math.random()}`,
            date,
            vaccine,
            veterinarian,
            _bdEventId: idEvent ?? null,
            _isNew: false,
          };
        })
        .sort((a, b) => {
          const da = a.date ? new Date(a.date).getTime() : 0;
          const db = b.date ? new Date(b.date).getTime() : 0;
          return da - db;
        });

      setCarnetRows(doneForPet);
    } catch (e) {
      console.error(e);
      setCarnetError("No se pudo cargar el carnet. Intenta nuevamente.");
      setCarnetRows([]);
    } finally {
      setLoadingCarnet(false);
    }
  };

  useEffect(() => {
    loadCarnet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePetId]);

  // =======================================================
  // CARNET: + AÑADIR (editar fecha, vacuna, veterinario)
  // =======================================================
  const [savingCarnet, setSavingCarnet] = useState(false);

  const [deleteMode, setDeleteMode] = useState(false);

  const [carnetVetiaModal, setCarnetVetiaModal] = useState({
  open: false,
  saving: false,
  error: "",
  success: "",
});

const [structuredRows, setStructuredRows] = useState([
  {
    id: makeRowId(),
    vaccineType: "",
    dose: "",
    date: "",
    veterinarian: "",
  },
]);

const [showCustomVaccines, setShowCustomVaccines] = useState(false);

const [customRows, setCustomRows] = useState([
  {
    id: makeRowId(),
    name: "",
    date: "",
    veterinarian: "",
  },
]);

  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
  open: false,
  row: null,
});

const toggleDeleteMode = () => {
  setDeleteMode((prev) => !prev);
};

  const addCarnetRow = () => {
  setCarnetVetiaModal({
    open: true,
    saving: false,
    error: "",
    success: "",
  });

  setStructuredRows([
    {
      id: makeRowId(),
      vaccineType: "",
      dose: "",
      date: "",
      veterinarian: "",
    },
  ]);

  setCustomRows([
    {
      id: makeRowId(),
      name: "",
      date: "",
      veterinarian: "",
    },
  ]);

  setShowCustomVaccines(false);
};

const closeCarnetVetiaModal = () => {
  if (carnetVetiaModal.saving) return;

  setCarnetVetiaModal({
    open: false,
    saving: false,
    error: "",
    success: "",
  });
};

const addStructuredRow = () => {
  setStructuredRows((prev) => [
    ...prev,
    {
      id: makeRowId(),
      vaccineType: "",
      dose: "",
      date: "",
      veterinarian: "",
    },
  ]);
};

const updateStructuredRow = (id, field, value) => {
  setStructuredRows((prev) =>
    prev.map((r) =>
      r.id === id
        ? {
            ...r,
            [field]: field === "veterinarian" ? upper(value) : value,
          }
        : r
    )
  );
};

const removeStructuredRow = (id) => {
  setStructuredRows((prev) =>
    prev.length > 1 ? prev.filter((r) => r.id !== id) : prev
  );
};

const addCustomRow = () => {
  setCustomRows((prev) => [
    ...prev,
    {
      id: makeRowId(),
      name: "",
      date: "",
      veterinarian: "",
    },
  ]);
};

const updateCustomRow = (id, field, value) => {
  setCustomRows((prev) =>
    prev.map((r) =>
      r.id === id
        ? {
            ...r,
            [field]: field === "veterinarian" ? upper(value) : value,
          }
        : r
    )
  );
};

const removeCustomRow = (id) => {
  setCustomRows((prev) =>
    prev.length > 1 ? prev.filter((r) => r.id !== id) : prev
  );
};

const buildStructuredPayload = () => {
  return structuredRows
    .map((r) => ({
      vaccineType: r.vaccineType,
      dose: r.dose,
      date: r.date || null,
      veterinarian: (r.veterinarian || "").trim(),
    }))
    .filter((r) => r.vaccineType && r.dose && r.date);
};

const buildCustomPayload = () => {
  return customRows
    .map((r) => ({
      name: (r.name || "").trim(),
      date: r.date || null,
      veterinarian: (r.veterinarian || "").trim(),
    }))
    .filter((r) => r.name && r.date);
};

const saveCarnetVaccinesWithVetia = async () => {
  const idUser = getCurrentUserId();

  if (!idUser || !safePetId) {
    setCarnetVetiaModal((prev) => ({
      ...prev,
      error: "No pude identificar el usuario o la mascota.",
    }));
    return;
  }

  const structuredVaccines = buildStructuredPayload();
  const customVaccines = buildCustomPayload();

  if (structuredVaccines.length === 0 && customVaccines.length === 0) {
    setCarnetVetiaModal((prev) => ({
      ...prev,
      error: "Añade al menos una vacuna con fecha.",
    }));
    return;
  }

  try {
    setCarnetVetiaModal((prev) => ({
      ...prev,
      saving: true,
      error: "",
      success: "",
    }));

    const payload = {
      petId: safePetId,
      idUser,
      species: getPetSpeciesForVaccines(safePet),
      birthDate:
        safePet?.birthDate ||
        safePet?.nacimiento ||
        safePet?.fechaNacimiento ||
        "",
      vaccineStatus: "ANOTADAS",
      structuredVaccines,
      customVaccines,
    };

    const res = await authFetch(`/vet-assistant/vaccines/plan/save`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const json = await res.json().catch(() => null);

    if (!res.ok || !json?.data) {
      throw new Error(
        json?.userMessage || "No se pudo generar el plan de vacunación."
      );
    }

    await loadCarnet();

    setCarnetVetiaModal((prev) => ({
      ...prev,
      saving: false,
      error: "",
      success:
        "Dra. Vetia analizó las vacunas de tu mascota y generó el esquema correspondiente en el calendario.",
    }));

    window.setTimeout(() => {
  setCarnetVetiaModal({
    open: false,
    saving: false,
    error: "",
    success: "",
  });
}, 1800);
  } catch (e) {
    console.error(e);
    setCarnetVetiaModal((prev) => ({
      ...prev,
      saving: false,
      error:
        "No se pudo guardar y analizar las vacunas. Revisa la conexión o el backend.",
    }));
  }
};

  const updateCarnetField = (rowId, field, value) => {
    setCarnetRows((prev) =>
      (Array.isArray(prev) ? prev : []).map((r) => (r.id === rowId ? { ...r, [field]: value } : r))
    );
  };

  const removeCarnetRow = (rowId) => {
    setCarnetRows((prev) => (Array.isArray(prev) ? prev : []).filter((r) => r.id !== rowId));
  };

  const deleteCarnetRowFromBackend = (row) => {
  if (!row) return;

  if (row._isNew) {
    removeCarnetRow(row.id);
    return;
  }

  setDeleteConfirmModal({
    open: true,
    row,
  });
};

const closeDeleteConfirmModal = () => {
  setDeleteConfirmModal({
    open: false,
    row: null,
  });
};

const confirmDeleteCarnetRow = async () => {
  const row = deleteConfirmModal.row;
  if (!row) return;

  const idEvent = row._bdEventId;

  if (!idEvent) {
    setCarnetError("No pude identificar esta vacuna para eliminarla.");
    window.setTimeout(() => setCarnetError(""), 2500);
    closeDeleteConfirmModal();
    return;
  }

  try {
    setSavingCarnet(true);
    setCarnetError("");

    const res = await authFetch(`/calendar/events/${idEvent}`, {
      method: "DELETE",
    });

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Backend respondió ${res.status}`);
    }

    await loadCarnet();
    closeDeleteConfirmModal();
  } catch (e) {
    console.error(e);
    setCarnetError("No se pudo eliminar la vacuna. Revisa el backend.");
    window.setTimeout(() => setCarnetError(""), 3200);
  } finally {
    setSavingCarnet(false);
  }
};


  // 🔥 Intenta crear el evento en backend (ajústalo si tu endpoint/body es distinto)
  const createVaccineEventInBackend = async ({ idUser, idPet, date, vaccine, veterinarian }) => {
    const payload = {
      idUser,
      idPet,
      eventType: "VACCINE",
      status: "HECHO",
      eventDate: date, // "YYYY-MM-DD"
      title: vaccine,
      veterinarian: veterinarian || "",
    };

    const res = await authFetch(`/calendar/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`No se pudo crear el evento (${res.status}). ${t}`);
    }

    return await res.json().catch(() => ({}));
  };

  const saveNewCarnetRow = async (row) => {
    const idUser = getCurrentUserId();
    if (!idUser || !safePetId) return;

    const date = (row.date || "").trim();
    const vaccine = (row.vaccine || "").trim();
    const veterinarian = (row.veterinarian || "").trim();

    if (!date || !vaccine) {
      setCarnetError("Completa al menos Fecha y Vacuna.");
      window.setTimeout(() => setCarnetError(""), 2200);
      return;
    }

    try {
      setSavingCarnet(true);
      setCarnetError("");

      await createVaccineEventInBackend({
        idUser,
        idPet: safePetId,
        date,
        vaccine,
        veterinarian,
      });

      await loadCarnet();
    } catch (e) {
      console.error(e);
      setCarnetError(
        "No pude guardar la vacuna en la BD. Si tu endpoint no es POST /calendar/events, dime cuál es y lo ajusto."
      );
      window.setTimeout(() => setCarnetError(""), 3200);
    } finally {
      setSavingCarnet(false);
    }
  };

  // =======================================================
  // QR público + PDF
  // =======================================================
  const [carnetPublicUrl, setCarnetPublicUrl] = useState("");
  const [loadingPublicId, setLoadingPublicId] = useState(false);

  const buildPublicUrl = (publicId) => {
    return `${window.location.origin}/carnet/public/${publicId}`;
  };

  const loadOrCreatePublicId = async () => {
    if (!safePetId) return;

    try {
      setLoadingPublicId(true);
      const res = await authFetch(`/pets/${safePetId}/carnet/public-id`);
      const json = await res.json();
      const publicId = json?.data?.publicId;
      if (publicId) setCarnetPublicUrl(buildPublicUrl(publicId));
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPublicId(false);
    }
  };

  // ✅ En desktop el carnet ahora vive en la pestaña "Carnet" (no en modal),
  // así que también generamos el QR cuando esa pestaña está activa.
  useEffect(() => {
    if (isVaccOpen || activeTab === "carnet") loadOrCreatePublicId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVaccOpen, activeTab, safePetId]);

  // =======================================================
  // PDF
  // =======================================================
  const downloadCarnetPdf = async () => {
    try {
      const doc = new jsPDF();

      await drawPetfamWatermark(doc);

      doc.setFillColor(0, 149, 169);
      doc.rect(0, 0, 210, 45, "F");

      const petName = safePet?.nombre || safePet?.name || "Mascota";
      const sexo = headerValues.sexo || "—";
      const peso = headerValues.peso
        ? `${String(headerValues.peso).replace(/kg/gi, "").trim()} kg`
        : "—";
      const nacimiento = headerValues.nacimiento || "—";

      doc.setFillColor(0, 149, 169);
      doc.rect(0, 0, 210, 45, "F");

      doc.setTextColor("#ffffff");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Carnet de vacunación", 14, 18);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text("www.petfam.club", 14, 24);

      doc.setFontSize(11);
      doc.text(petName, 14, 30);

      if (heroImg) {
        try {
          const imgBase64 = await imageUrlToBase64(heroImg);

          const imgW = 34;
          const imgH = 34;
          const imgX = 158;
          const imgY = 6;

          const cx = imgX + imgW / 2;
          const cy = imgY + imgH / 2;
          const r = 20;

          doc.setFillColor(255, 255, 255);
          doc.circle(cx, cy, r, "F");

          doc.setDrawColor(210, 210, 210);
          doc.setLineWidth(0.8);
          doc.circle(cx, cy, r, "S");

          const isPng = String(imgBase64).startsWith("data:image/png");
          doc.addImage(imgBase64, isPng ? "PNG" : "JPEG", imgX, imgY, imgW, imgH);
        } catch (e) {
          console.warn("No se pudo cargar la imagen para el PDF", e);
        }
      }

      doc.setTextColor("#0B2F3E");
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(10, 40, 190, 22, 8, 8, "F");

      doc.setFontSize(10);
      doc.text(`Sexo: ${sexo}`, 20, 54);
      doc.text(`Peso: ${peso}`, 80, 54);
      doc.text(`Nacimiento: ${nacimiento}`, 135, 54);

      const rows = (carnetRows || []).map((r) => [r.date || "", r.vaccine || "", r.veterinarian || "—"]);

      autoTable(doc, {
        startY: 70,
        head: [["Fecha", "Vacuna", "Veterinario"]],
        body: rows.length ? rows : [["—", "Aún no hay vacunas HECHO", "—"]],
        styles: { fontSize: 10, cellPadding: 6 },
        headStyles: { fillColor: [0, 149, 169], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 14, right: 14 },
      });

      doc.save(`carnet-${petName}.pdf`);
    } catch (err) {
      console.error(err);
      alert("No se pudo generar el PDF. Intenta nuevamente.");
    }
  };

  return (
    <main className={`petframe ${animClass}`} onAnimationEnd={handleAnimationEnd}>
      <button className="close-btn" onClick={handleClose}>
        <X size={18} />
      </button>

      {loadingPet && (
        <div style={{ padding: 18, color: "#0b2f3e", fontFamily: "Poppins" }}>
          Cargando información de tu mascota…
        </div>
      )}

      {petError && (
        <div style={{ padding: 18, color: "#c0392b", fontFamily: "Poppins" }}>
          {petError}
        </div>
      )}

      {!petError && (
        <>
          <input style={{ display: "none" }} type="file" accept="image/*" onChange={() => {}} />

          <div className="desktop-layout">
            {/* ===================== TARJETA ÚNICA (encabezado + pestañas + contenido) ===================== */}
            <div className="pf-card-shell">
              <div className="kitten-card">
                <div className="card-topbar">
                  <span className="pf-badge">
                    <span className="pf-badge-dot" />
                    Perfil de mascota
                  </span>

                  <div className="pf-top-icons">
                    <button
                      type="button"
                      className="pf-icon-btn"
                      onClick={handleClose}
                      title="Cerrar"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>

                <div className="card-body">
                  <div className="left-col">
                    <p className="pf-meta">
                      {(safePet.raza || safePet.breed || "").toString().toUpperCase()}
                      {safePet.raza || safePet.breed ? " · " : ""}
                      {ageLabel}
                    </p>

                    <h1 className="pf-name">{safePet.nombre || safePet.name || "Mascota"}</h1>

                    <div className="pf-actions-row">
                      <button type="button" className="pf-btn-secondary" onClick={toggleHeaderEdit}>
                        <Pencil size={16} />
                        {headerEditing ? "Guardar" : "Editar"}
                      </button>
                    </div>
                  </div>

                  <div className="pf-health-ring" style={{ position: "relative" }}>
                    <svg viewBox="0 0 120 120" className="pf-health-svg">
                      <circle className="pf-health-track" cx="60" cy="60" r="52" />
                      <circle
                        className="pf-health-value-circle"
                        cx="60"
                        cy="60"
                        r="52"
                        style={{
                          strokeDasharray: 2 * Math.PI * 52,
                          strokeDashoffset: 2 * Math.PI * 52 * (1 - animatedSalud / 100),
                        }}
                      />
                    </svg>
                    <div className="pf-health-text">
                      <span className="pf-health-percent">
                        {saludPercent != null ? saludPercent : 0}%
                      </span>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <span className="pf-health-label">CUIDADOS</span>
                        <button
                          type="button"
                          onClick={() => setHealthInfoOpen((v) => !v)}
                          aria-label="¿Cómo se calcula este porcentaje?"
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            border: "1px solid rgba(255,255,255,.6)",
                            background: "rgba(255,255,255,.08)",
                            color: "#fff",
                            fontSize: 9,
                            lineHeight: "12px",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            flexShrink: 0,
                          }}
                        >
                          ?
                        </button>
                      </span>
                    </div>

                    {healthInfoOpen && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: "absolute",
                          top: "calc(100% + 10px)",
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: 220,
                          zIndex: 20,
                          background: "#fff",
                          color: "#0B2F3E",
                          borderRadius: 14,
                          padding: "12px 14px",
                          fontSize: 12,
                          fontWeight: 500,
                          lineHeight: 1.4,
                          textAlign: "center",
                          boxShadow: "0 14px 34px rgba(0,0,0,.28)",
                        }}
                      >
                        Este % combina las tareas de hoy completadas y si las vacunas y desparasitaciones de {safePet.nombre || safePet.name || "tu mascota"} están al día.
                      </div>
                    )}
                  </div>

                  <img
                    className="hero-dog"
                    src={heroImg}
                    alt={safePet.nombre || safePet.name}
                    decoding="async"
                    fetchPriority="high"
                  />
                </div>
              </div>

              {/* ===================== PANEL INFERIOR (misma tarjeta) ===================== */}
              <div className="pf-panel">
                <div className="pf-tabs">
                  <button
                    type="button"
                    className={`pf-tab-btn ${activeTab === "resumen" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("resumen")}
                  >
                    Resumen
                  </button>
                  <button
                    type="button"
                    className={`pf-tab-btn ${activeTab === "salud" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("salud")}
                  >
                    Salud
                  </button>
                  <button
                    type="button"
                    className={`pf-tab-btn ${activeTab === "carnet" ? "is-active" : ""}`}
                    onClick={() => setActiveTab("carnet")}
                  >
                    Carnet
                  </button>
                </div>

                <div className="pf-tab-content">
                  {/* ===================== RESUMEN ===================== */}
                  {activeTab === "resumen" && (
                    <>
                      <div className="pf-stat-grid">
                        <div className="pf-stat-card">
                          <div className="pf-stat-icon">
                            <ShieldCheck size={18} />
                          </div>
                          <span className="pf-stat-label">SEXO</span>
                          <span className="pf-stat-value">{headerValues.sexo || "—"}</span>
                        </div>

                        <div className="pf-stat-card">
                          <div className="pf-stat-icon">
                            <Scale size={18} />
                          </div>
                          <span className="pf-stat-label">PESO</span>
                          {headerEditing ? (
                            <input
                              className="pf-stat-input"
                              value={headerValues.peso}
                              onChange={(e) => handleHeaderChange("peso", e.target.value)}
                              inputMode="decimal"
                              placeholder="Peso (kg)"
                            />
                          ) : (
                            <span className="pf-stat-value">
                              {headerValues.peso
                                ? `${String(headerValues.peso).replace(/kg/gi, "").trim()} kg`
                                : "—"}
                            </span>
                          )}
                        </div>

                        <div className="pf-stat-card">
                          <div className="pf-stat-icon">
                            <Cake size={18} />
                          </div>
                          <span className="pf-stat-label">CUMPLEAÑOS</span>
                          <span className="pf-stat-value">{headerValues.nacimiento || "—"}</span>
                        </div>
                      </div>

                      {/* ✅ Agenda real: vacunas/desparasitaciones + tareas de hoy */}
                      <div className="pf-agenda-block">
                        <h3 className="pf-agenda-title">Agenda de {safePet.nombre || safePet.name || "tu mascota"}</h3>

                        {loadingAgenda && petDailyTasks.length === 0 && agendaEvents.length === 0 ? (
                          <p className="pf-agenda-empty">Cargando agenda…</p>
                        ) : petDailyTasks.length === 0 && agendaEvents.length === 0 ? (
                          <p className="pf-agenda-empty">
                            No hay tareas ni vacunas programadas por ahora.
                          </p>
                        ) : (
                          <div className="pf-agenda-list">
                            {petDailyTasks.map((task) => (
                              <div className="pf-agenda-item" key={`task-${task.id}`}>
                                <span className={`pf-agenda-check ${task.done ? "is-done" : ""}`}>
                                  {task.done ? <Check size={16} /> : <Check size={16} style={{ opacity: 0.35 }} />}
                                </span>
                                <div className="pf-agenda-item-text">
                                  <span className="pf-agenda-item-title">{task.title}</span>
                                  <span className="pf-agenda-item-date">Hoy</span>
                                </div>
                              </div>
                            ))}

                            {agendaEvents.map((ev) => (
                              <div className="pf-agenda-item" key={ev.id}>
                                <span className={`pf-agenda-check ${ev.done ? "is-done" : ""}`}>
                                  {ev.done ? <Check size={16} /> : <Syringe size={15} />}
                                </span>
                                <div className="pf-agenda-item-text">
                                  <span className="pf-agenda-item-title">{ev.title}</span>
                                  <span className="pf-agenda-item-date">{formatDateShort(ev.date)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* ===================== SALUD ===================== */}
                  {activeTab === "salud" && (
                    <div className="pf-salud-wrap">
                      <div className="pf-indicators-card">
                        <span className="pf-indicators-title">Indicadores</span>

                        {/* Vacunas al día */}
                        <div className="pf-indicator-row">
                          <div className="pf-indicator-head">
                            <span>Vacunas al día</span>
                            <span>{vaccineStats ? `${vaccineStats.percent}%` : "—"}</span>
                          </div>
                          <div className="pf-indicator-bar">
                            <div
                              className="pf-indicator-bar-fill"
                              style={{ width: `${vaccineStats ? vaccineStats.percent : 0}%` }}
                            />
                          </div>
                          {!vaccineStats && (
                            <span className="pf-indicator-caption">
                              Sin vacunas registradas en el calendario todavía.
                            </span>
                          )}
                        </div>

                        {/* Desparasitaciones al día */}
                        <div className="pf-indicator-row">
                          <div className="pf-indicator-head">
                            <span>Desparasitaciones al día</span>
                            <span>{dewormingStats ? `${dewormingStats.percent}%` : "—"}</span>
                          </div>
                          <div className="pf-indicator-bar">
                            <div
                              className="pf-indicator-bar-fill"
                              style={{ width: `${dewormingStats ? dewormingStats.percent : 0}%` }}
                            />
                          </div>
                          {!dewormingStats && (
                            <span className="pf-indicator-caption">
                              Sin desparasitaciones registradas en el calendario todavía.
                            </span>
                          )}
                        </div>

                        {/* Actividad de hoy (dato real, ver nota) */}
                        <div className="pf-indicator-row">
                          <div className="pf-indicator-head">
                            <span>Actividad de hoy</span>
                            <span>{activityStats ? `${activityStats.percent}%` : "—"}</span>
                          </div>
                          <div className="pf-indicator-bar">
                            <div
                              className="pf-indicator-bar-fill"
                              style={{ width: `${activityStats ? activityStats.percent : 0}%` }}
                            />
                          </div>
                          {activityStats ? (
                            <span className="pf-indicator-caption">
                              {activityStats.done} de {activityStats.total} cuidados de hoy completados.
                            </span>
                          ) : (
                            <span className="pf-indicator-caption">
                              Aún no hay cuidados de hoy asignados a esta mascota.
                            </span>
                          )}
                        </div>

                        {/* Análisis de peso vs. estándar de raza */}
                        <div className="pf-indicator-row">
                          <div className="pf-indicator-head">
                            <span>{weightAnalysis?.known ? weightAnalysis.status : "Peso"}</span>
                            <span>{weightAnalysis?.known ? `${weightAnalysis.percent}%` : "—"}</span>
                          </div>
                          <div className="pf-indicator-bar">
                            <div
                              className="pf-indicator-bar-fill"
                              style={{ width: `${weightAnalysis?.known ? weightAnalysis.percent : 0}%` }}
                            />
                          </div>
                          {weightAnalysis?.known ? (
                            <span className="pf-indicator-caption">
                              {weightAnalysis.weight} kg registrados · rango estándar para la raza:{" "}
                              {weightAnalysis.min}–{weightAnalysis.max} kg.
                            </span>
                          ) : (
                            <span className="pf-indicator-caption">
                              {headerValues.peso
                                ? "No tenemos el rango de peso estándar para esta raza todavía."
                                : "Registra el peso y la raza para ver el análisis."}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ===================== CARNET ===================== */}
                  {activeTab === "carnet" && (
                    <div className={`pf-carnet-flip ${carnetOpen ? "is-open" : ""}`}>
                      <div className="pf-carnet-cover-wrap">
                        <div className="pf-carnet-cover">
                          <div className="pf-carnet-cover-head">
                            <span className="pf-carnet-cover-label">Carnet digital</span>
                            <ShieldCheck size={18} />
                          </div>

                          <h2 className="pf-carnet-cover-name">
                            {safePet.nombre || safePet.name || "Mascota"}
                          </h2>

                          <p className="pf-carnet-cover-meta">
                            {[
                              safePet.raza || safePet.breed,
                              headerValues.sexo,
                              headerValues.peso
                                ? `${String(headerValues.peso).replace(/kg/gi, "").trim()} kg`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>

                          <div className="pf-carnet-cover-info">
                            <span className="pf-carnet-cover-info-label">Nacimiento</span>
                            <span className="pf-carnet-cover-info-value">
                              {headerValues.nacimiento || "—"}
                            </span>
                          </div>

                          <button
                            type="button"
                            className="pf-carnet-open-btn"
                            onClick={() => setCarnetOpen(true)}
                          >
                            <ArrowRight size={16} />
                            Abrir carnet
                          </button>
                        </div>

                        <p className="pf-carnet-cover-caption">
                          Incluye identificación y esquema de vacunación al día.
                        </p>
                      </div>

                      <div className="pf-carnet-inner">
                        <button
                          type="button"
                          className="pf-carnet-back-btn"
                          onClick={() => setCarnetOpen(false)}
                        >
                          ← Volver a la portada
                        </button>

                        <div className="pf-carnet-panel">
                          <CarnetContent
                            API_BASE_URL={API_BASE_URL}
                            carnetRows={carnetRows}
                            loadingCarnet={loadingCarnet}
                            carnetError={carnetError}
                            savingCarnet={savingCarnet}
                            addCarnetRow={addCarnetRow}
                            updateCarnetField={updateCarnetField}
                            saveNewCarnetRow={saveNewCarnetRow}
                            removeCarnetRow={removeCarnetRow}
                            deleteCarnetRowFromBackend={deleteCarnetRowFromBackend}
                            deleteMode={deleteMode}
                            toggleDeleteMode={toggleDeleteMode}
                            setCarnetRows={setCarnetRows}
                            loadingPublicId={loadingPublicId}
                            carnetPublicUrl={carnetPublicUrl}
                            downloadCarnetPdf={downloadCarnetPdf}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <section className="pet-mobile">
            <div className="petm-carnet-wrapper">
              <button type="button" className="petm-carnet-btn" onClick={() => setIsVaccOpen(true)}>
                Ver carnet
              </button>
            </div>

            <div className="petm-top">
              <div style={{ cursor: "default" }}>
                <img
                  src={heroImg}
                  alt={safePet.nombre || safePet.name}
                  className="petm-dog-img"
                  decoding="async"
                />
              </div>

              <div className="petm-arc">
                <h1 className="petm-name">{safePet.nombre || safePet.name}</h1>
                <p className="petm-age">{ageLabel}</p>
              </div>
            </div>

            <div className="petm-body">
              <div className="petm-breed-row">
                <span className="petm-breed">{safePet.raza || safePet.breed}</span>

                <button className="petm-edit-btn" onClick={toggleHeaderEdit}>
                  {headerEditing ? <Check size={16} /> : <Pencil size={16} />}
                </button>
              </div>

              <div className="petm-pills">
                <div className="petm-pill">
                  <span className="pill-main">{headerValues.sexo || "Sexo"}</span>
                  <span className="pill-label">Sexo</span>
                </div>

                <div className="petm-pill">
                  {headerEditing ? (
                    <>
                      <input
                        className="pill-input"
                        value={headerValues.peso}
                        onChange={(e) => handleHeaderChange("peso", e.target.value)}
                        inputMode="decimal"
                        placeholder="Peso (kg)"
                      />
                      <span className="pill-label">Peso</span>
                    </>
                  ) : (
                    <>
                      <span className="pill-main">
                        {headerValues.peso
                          ? `${String(headerValues.peso).replace(/kg/gi, "").trim()} kg`
                          : "Peso"}
                      </span>
                      <span className="pill-label">Peso</span>
                    </>
                  )}
                </div>

                <div className="petm-pill">
                  <span className="pill-main">{headerValues.nacimiento || "Nacimiento"}</span>
                  <span className="pill-label">Nacimiento</span>
                </div>
              </div>
            </div>

            {isVaccOpen && (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(0,0,0,.45)",
                  backdropFilter: "blur(8px)",
                  zIndex: 9999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 15,
                }}
                onClick={() => setIsVaccOpen(false)}
              >
                <div
                  style={{
                    width: "min(980px, 96vw)",
                    maxHeight: "86vh",
                    background: "#fff",
                    borderRadius: 22,
                    overflow: "auto",
                    WebkitOverflowScrolling: "touch",
                    boxShadow: "0 20px 60px rgba(0,0,0,.35)",
                    position: "relative",
                    padding: 16,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => setIsVaccOpen(false)}
                    style={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      width: 34,
                      height: 34,
                      borderRadius: 999,
                      border: "none",
                      background: "#fff",
                      boxShadow: "0 10px 26px rgba(0,0,0,.18)",
                      display: "grid",
                      placeItems: "center",
                      cursor: "pointer",
                      color: "#0B2F3E",
                      zIndex: 5,
                    }}
                  >
                    <X size={14} />
                  </button>

                  <div>
                    <CarnetContent
                      API_BASE_URL={API_BASE_URL}
                      carnetRows={carnetRows}
                      loadingCarnet={loadingCarnet}
                      carnetError={carnetError}
                      savingCarnet={savingCarnet}
                      addCarnetRow={addCarnetRow}
                      updateCarnetField={updateCarnetField}
                      saveNewCarnetRow={saveNewCarnetRow}
                      removeCarnetRow={removeCarnetRow}
deleteCarnetRowFromBackend={deleteCarnetRowFromBackend}
deleteMode={deleteMode}
toggleDeleteMode={toggleDeleteMode}
setCarnetRows={setCarnetRows}
                      loadingPublicId={loadingPublicId}
                      carnetPublicUrl={carnetPublicUrl}
                      downloadCarnetPdf={downloadCarnetPdf}
                    />
                  </div>
                </div>
              </div>
            )}
          </section>

          {false && (
            <div className="cropper-overlay">
              <div className="cropper-modal">
                <div className="cropper-area">
                  <Cropper image={null} crop={{ x: 0, y: 0 }} zoom={1} aspect={1} />
                </div>
              </div>
            </div>
          )}

          {deleteConfirmModal.open &&
  createPortal(
    <div className="pf-delete-overlay" onClick={closeDeleteConfirmModal}>
      <div className="pf-delete-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="pf-delete-close"
          onClick={closeDeleteConfirmModal}
        >
          <X size={18} />
        </button>

        <div className="pf-delete-icon">
          <Trash2 size={26} />
        </div>

        <h3>Eliminar vacuna</h3>

        <p>¿Seguro que quieres eliminar esta vacuna del carnet?</p>

        <div className="pf-delete-vaccine">
          <strong>{deleteConfirmModal.row?.vaccine || "Vacuna"}</strong>
          <span>{deleteConfirmModal.row?.date || ""}</span>
        </div>

        <div className="pf-delete-actions">
          <button type="button" onClick={closeDeleteConfirmModal}>
            Cancelar
          </button>

          <button
            type="button"
            onClick={confirmDeleteCarnetRow}
            disabled={savingCarnet}
          >
            {savingCarnet ? "Eliminando..." : "Sí, eliminar"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )}

  {carnetVetiaModal.open &&
  createPortal(
    <div className="pf-delete-overlay" onClick={closeCarnetVetiaModal}>
      <div
        className="pf-delete-modal carnet-vetia-modal"
        style={{ width: "min(760px, 94vw)", maxHeight: "85vh", overflowY: "auto", WebkitOverflowScrolling: "touch", }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="pf-delete-close"
          onClick={closeCarnetVetiaModal}
          disabled={carnetVetiaModal.saving}
        >
          <X size={18} />
        </button>

        <div className="pf-delete-icon" style={{ background: "#0095a9", color: "white" }}>
          <Syringe size={26} />
        </div>

        <h3>Añadir vacunas al carnet</h3>

        <p>
          Registra las vacunas aplicadas.
        </p>

        <div style={{ marginTop: 14, textAlign: "left" }}>
          <h4 style={{ color: "#0B2F3E", marginBottom: 10 }}>
            Vacunas del esquema
          </h4>

          <div className="vet-items">
            {structuredRows.map((row) => (
              <div key={row.id} className="vet-structured-row">
                <select
  style={carnetInputStyle}
  value={row.vaccineType}
  onChange={(e) =>
    updateStructuredRow(row.id, "vaccineType", e.target.value)
  }
>
                  <option value="">Tipo de vacuna</option>

                  {getPetSpeciesForVaccines(safePet) === "PERRO" ? (
                    <>
                      <option value="POLIVALENTE_CANINA">Polivalente canina</option>
                      <option value="RABIA">Rabia</option>
                    </>
                  ) : (
                    <>
                      <option value="TRIPLE_FELINA">Triple felina</option>
                      <option value="RABIA">Rabia</option>
                    </>
                  )}
                </select>

                <select
  style={carnetInputStyle}
  value={row.dose}
  onChange={(e) =>
    updateStructuredRow(row.id, "dose", e.target.value)
  }
>
                  <option value="">Dosis</option>
                  <option value="FIRST">1ª dosis</option>
                  <option value="SECOND">2ª dosis</option>
                  <option value="THIRD">3ª dosis</option>
                  <option value="ANNUAL">Refuerzo anual</option>
                </select>

<CarnetDateField
  value={row.date}
  onChange={(value) => updateStructuredRow(row.id, "date", value)}
/>

                <input
  style={carnetInputStyle}
  value={row.veterinarian}
  onChange={(e) =>
    updateStructuredRow(row.id, "veterinarian", e.target.value)
  }
  placeholder="Veterinario"
/>

                <button
                  type="button"
                  className="vet-icon-btn"
                  onClick={() => removeStructuredRow(row.id)}
                  disabled={structuredRows.length === 1}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>

          <button
  type="button"
  onClick={addStructuredRow}
  style={{
    marginTop: 10,
    background: "transparent",
    border: "none",
    color: "#0F6B7A",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
  }}
>
  <Plus size={14} />
  Añadir vacuna
</button>

          <button
            type="button"
            onClick={() => setShowCustomVaccines((s) => !s)}
            className="vet-link-btn"
          >
            <Plus size={14} />
            Añadir otra vacuna diferente
          </button>

          {showCustomVaccines && (
            <div className="custom-vaccines-wrap">
              <h4 style={{ color: "#0B2F3E", margin: "12px 0 10px" }}>
                Otras vacunas
              </h4>

              {customRows.map((row) => (
                <div key={row.id} className="vet-structured-row">
                  <input
                    value={row.name}
                    onChange={(e) =>
                      updateCustomRow(row.id, "name", e.target.value)
                    }
                    placeholder="Nombre de la vacuna"
                  />

                  <div />

                  <CarnetDateField
  value={row.date}
  onChange={(value) => updateCustomRow(row.id, "date", value)}
/>

                  <input
                    value={row.veterinarian}
                    onChange={(e) =>
                      updateCustomRow(row.id, "veterinarian", e.target.value)
                    }
                    placeholder="Veterinario"
                  />

                  <button
                    type="button"
                    className="vet-icon-btn"
                    onClick={() => removeCustomRow(row.id)}
                    disabled={customRows.length === 1}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}

              <button type="button" onClick={addCustomRow} className="vet-add-btn">
                <Plus size={16} /> Añadir otra vacuna diferente
              </button>
            </div>
          )}
        </div>

        {carnetVetiaModal.error && (
          <p style={{ color: "#C0392B", fontWeight: 700, marginTop: 12 }}>
            {carnetVetiaModal.error}
          </p>
        )}

        {carnetVetiaModal.success && (
          <div
            style={{
              marginTop: 14,
              padding: 12,
              borderRadius: 16,
              background: "#E9F8F1",
              color: "#116B45",
              fontWeight: 700,
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            <ShieldCheck size={18} />
            {carnetVetiaModal.success}
          </div>
        )}

        <div className="pf-delete-actions">
          <button
            type="button"
            onClick={closeCarnetVetiaModal}
            disabled={carnetVetiaModal.saving}
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={saveCarnetVaccinesWithVetia}
            disabled={carnetVetiaModal.saving}
          >
            {carnetVetiaModal.saving
              ? "Analizando..."
              : "Guardar"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )}
        </>
      )}
    </main>
  );
}